import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useLanguage } from '../context/LanguageContext'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { 
  ArrowLeft, Star, MessageSquare, Bug, Lightbulb, Sparkles, HelpCircle, 
  Smartphone, Globe, RefreshCw, User, Mail, Calendar, Filter,
  BarChart3, CheckCircle, Clock, Eye, Copy
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function FeedbackAdminPage() {
  const navigate = useNavigate()
  const { locale } = useLanguage()
  const { token, user } = useAuth()
  const isSpanish = locale === 'es-MX'
  
  const [feedback, setFeedback] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')

  const categoryIcons = {
    bug: Bug,
    improvement: Sparkles,
    idea: Lightbulb,
    general: HelpCircle
  }

  const categoryColors = {
    bug: 'text-red-500 bg-red-500/10',
    improvement: 'text-blue-500 bg-blue-500/10',
    idea: 'text-amber-500 bg-amber-500/10',
    general: 'text-gray-500 bg-gray-500/10'
  }

  const statusColors = {
    new: 'bg-blue-500',
    reviewed: 'bg-amber-500',
    resolved: 'bg-emerald-500'
  }

  const usageIcons = {
    app: Smartphone,
    web: Globe,
    both: RefreshCw
  }

  useEffect(() => {
    fetchFeedback()
    fetchStats()
  }, [statusFilter])

  const fetchFeedback = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {}
      const response = await axios.get(`${API}/feedback`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      })
      setFeedback(response.data)
    } catch (error) {
      console.error('Error fetching feedback:', error)
      if (error.response?.status === 403) {
        toast.error(isSpanish ? 'Acceso denegado' : 'Access denied')
        navigate('/')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/feedback/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await axios.put(`${API}/feedback/${feedbackId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      toast.success(isSpanish ? 'Estado actualizado' : 'Status updated')
      fetchFeedback()
      fetchStats()
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error(isSpanish ? 'Error al actualizar' : 'Error updating')
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getCategoryLabel = (category) => {
    const labels = {
      bug: 'Bug',
      improvement: isSpanish ? 'Mejora' : 'Improvement',
      idea: isSpanish ? 'Idea' : 'Idea',
      general: 'General'
    }
    return labels[category] || category
  }

  const getUsageLabel = (usage) => {
    const labels = {
      app: isSpanish ? 'App móvil' : 'Mobile app',
      web: isSpanish ? 'Navegador' : 'Browser',
      both: isSpanish ? 'Ambos' : 'Both'
    }
    return labels[usage] || usage
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {isSpanish ? 'Cargando...' : 'Loading...'}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center gap-3 p-4 max-w-4xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">
              {isSpanish ? 'Feedback Recibido' : 'Received Feedback'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {isSpanish ? 'Panel de administración' : 'Admin panel'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 pb-24">
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-xs text-muted-foreground">
                      {isSpanish ? 'Total' : 'Total'}
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-muted-foreground/50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.average_rating}</p>
                    <p className="text-xs text-muted-foreground">
                      {isSpanish ? 'Promedio' : 'Average'}
                    </p>
                  </div>
                  <Star className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.by_status?.new || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {isSpanish ? 'Nuevos' : 'New'}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.by_status?.resolved || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {isSpanish ? 'Resueltos' : 'Resolved'}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Usage Stats */}
        {stats && stats.by_preferred_usage && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">
                {isSpanish ? 'Preferencia de uso' : 'Usage preference'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-primary" />
                  <span className="text-sm">App: <strong>{stats.by_preferred_usage.app || 0}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <span className="text-sm">Web: <strong>{stats.by_preferred_usage.web || 0}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  <span className="text-sm">{isSpanish ? 'Ambos' : 'Both'}: <strong>{stats.by_preferred_usage.both || 0}</strong></span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter */}
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
              <SelectItem value="new">{isSpanish ? 'Nuevos' : 'New'}</SelectItem>
              <SelectItem value="reviewed">{isSpanish ? 'Revisados' : 'Reviewed'}</SelectItem>
              <SelectItem value="resolved">{isSpanish ? 'Resueltos' : 'Resolved'}</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {feedback.length} {isSpanish ? 'resultados' : 'results'}
          </span>
        </div>

        {/* Feedback List */}
        <div className="space-y-4">
          {feedback.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">
                  {isSpanish ? 'No hay feedback aún' : 'No feedback yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            feedback.map((fb) => {
              const CategoryIcon = categoryIcons[fb.category] || HelpCircle
              const UsageIcon = usageIcons[fb.preferred_usage] || RefreshCw
              
              return (
                <Card key={fb.id} data-testid={`feedback-card-${fb.id}`}>
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {/* Rating */}
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star}
                              className={`w-4 h-4 ${
                                star <= fb.rating 
                                  ? 'fill-amber-400 text-amber-400' 
                                  : 'text-muted-foreground/20'
                              }`} 
                            />
                          ))}
                        </div>
                        
                        {/* Category Badge */}
                        <div className={`px-2 py-1 rounded-full flex items-center gap-1 ${categoryColors[fb.category]}`}>
                          <CategoryIcon className="w-3 h-3" />
                          <span className="text-xs font-medium">{getCategoryLabel(fb.category)}</span>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <Badge 
                        variant="secondary" 
                        className={`${statusColors[fb.status]} text-white`}
                      >
                        {fb.status === 'new' && (isSpanish ? 'Nuevo' : 'New')}
                        {fb.status === 'reviewed' && (isSpanish ? 'Revisado' : 'Reviewed')}
                        {fb.status === 'resolved' && (isSpanish ? 'Resuelto' : 'Resolved')}
                      </Badge>
                    </div>
                    
                    {/* Message */}
                    <p className="text-sm mb-3 whitespace-pre-wrap">{fb.message}</p>
                    
                    {/* Context (if provided) */}
                    {fb.context && (
                      <div className="mb-4 p-3 bg-muted/50 rounded-lg border-l-2 border-primary/50">
                        <p className="text-xs text-muted-foreground mb-1 font-medium">
                          {isSpanish ? '¿Qué estaba intentando hacer?' : 'What were they trying to do?'}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{fb.context}</p>
                      </div>
                    )}
                    
                    {/* Subscription Interest (if provided) */}
                    {fb.subscription_interest && (
                      <div className="mb-4 p-3 bg-emerald-500/10 rounded-lg border-l-2 border-emerald-500">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-1 font-medium">
                          Interés en suscripción
                        </p>
                        <p className="text-sm font-medium">
                          {fb.subscription_interest === 'free' && 'Prefiere versión gratuita'}
                          {fb.subscription_interest === 'mxn_49' && '$49 MXN / mes'}
                          {fb.subscription_interest === 'mxn_99' && '$99 MXN / mes'}
                          {fb.subscription_interest === 'mxn_199' && '$199 MXN / mes'}
                          {fb.subscription_interest === 'mxn_299_plus' && '$299 MXN o más / mes'}
                        </p>
                        {fb.subscription_comment && (
                          <p className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">
                            "{fb.subscription_comment}"
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* User Info & Usage */}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-3">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="font-medium text-foreground">{fb.user_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span className="font-medium text-foreground">{fb.user_email}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-5 px-1 ml-1"
                          onClick={() => {
                            navigator.clipboard.writeText(fb.user_email)
                            toast.success('Email copiado')
                          }}
                          data-testid={`copy-email-${fb.id}`}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(fb.created_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <UsageIcon className="w-3 h-3" />
                        {getUsageLabel(fb.preferred_usage)}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={() => navigate(`/testers?email=${encodeURIComponent(fb.user_email)}`)}
                        data-testid={`view-tester-${fb.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Ver tester
                      </Button>
                      <div className="flex-1" />
                      <Button 
                        size="sm" 
                        variant={fb.status === 'new' ? 'default' : 'outline'}
                        onClick={() => updateStatus(fb.id, 'new')}
                        disabled={fb.status === 'new'}
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        {isSpanish ? 'Nuevo' : 'New'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant={fb.status === 'reviewed' ? 'default' : 'outline'}
                        onClick={() => updateStatus(fb.id, 'reviewed')}
                        disabled={fb.status === 'reviewed'}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {isSpanish ? 'Revisado' : 'Reviewed'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant={fb.status === 'resolved' ? 'default' : 'outline'}
                        onClick={() => updateStatus(fb.id, 'resolved')}
                        disabled={fb.status === 'resolved'}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {isSpanish ? 'Resuelto' : 'Resolved'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
